import { yupResolver } from '@hookform/resolvers/yup';
import { AddCircleOutlineOutlined, RemoveCircleOutlineOutlined, HelpOutline } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box, Button, Divider, FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextareaAutosize, TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { ChangeEvent, FC, memo, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { appBarHeight } from 'src/constants/layout';
import LicenseContext from 'src/contexts/LicenseContext';
import { CompanyOrderTimeRange, CompanyOrderTimeRangeKinds, DeliveryListGroupingValues, LicenseEntity, LoadingUnloadingDuration } from 'src/entities/licenseEntity';
import { useQueryCompany } from 'src/hooks/useQueryCompany';
import { datetimeUtil } from 'src/utils/datetime.util';
import stringUtil from 'src/utils/string.util';
import * as yup from 'yup';

import arrayUtil from '../../utils/array.util';

const bigTruckCombination = (_value: number, context: yup.TestContext) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const {
    big_weight_truck_threshold, big_volume_truck_threshold,
    big_weight_order_threshold, big_volume_order_threshold,
    small_weight_order_threshold, small_volume_order_threshold
  } = context.parent;
  if ((big_weight_order_threshold || big_volume_order_threshold
    || small_weight_order_threshold || small_volume_order_threshold)
    && (!big_weight_truck_threshold && !big_volume_truck_threshold)) {
      return false;
  }
  return true;
};

const companyOrderTimeRange = yup.object({
  start_at: yup
    .string()
    .nullable()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, '時間はHH:mm形式で入力してください')
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value) => (value === '' ? null : value)),
  end_at: yup
    .string()
    .nullable()
    .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, '時間はHH:mm形式で入力してください')
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value) => (value === '' ? null : value)),
}).test('both-or-none', '開始時間・終了時間の両方を入力するか、両方を空にしてください', (value) => {
  const { start_at: startAt, end_at: endAt } = value;
  return (!!startAt && !!endAt) || (!startAt && !endAt);
});

const packingStyleSchema = yup.object({
  name: yup.string().required(),
  weight: yup
    .number()
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  volume: yup
    .number()
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
});

const patternTwoDigisAfterComma = /^\d+(\.\d{0,2})?$/;
const digitPointValidator = (val: unknown, _context: yup.TestContext) => {
  if (val !== undefined && val !== null && stringUtil.toText(val).trim() !== '') {
    return patternTwoDigisAfterComma.test(stringUtil.toText(val));
  }
  return true;
};

const schema = yup.object({
  orders_limit_per_driver: yup
    .number()
    .integer('整数で入力してください')
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  shuttle_limit: yup
    .number()
    .integer('整数で入力してください')
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  unloading_position_limit_by_a_shuttle: yup
    .number()
    .integer('整数で入力してください')
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  driver_cost_yen_per_hours: yup
    .number()
    .integer('整数で入力してください')
    .min(0, '0未満は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  truck_expressway_fee_yen_per_shift: yup
    .number()
    .integer('整数で入力してください')
    .min(0, '0未満は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  truck_fuel_cost_yen_per_km: yup
    .number()
    .integer('整数で入力してください')
    .min(0, '0未満は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  truck_insurance_fee_yen_per_day: yup
    .number()
    .integer('整数で入力してください')
    .min(0, '0未満は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  truck_repair_cost_yen_per_day: yup
    .number()
    .integer('整数で入力してください')
    .min(0, '0未満は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  packing_style_and_weight_volumes: yup.array().of(packingStyleSchema),
  big_weight_truck_threshold: yup
    .number()
    .integer('整数で入力してください')
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
    .test(
      'combination_validation',
      '詳細条件を設定した場合は、大型車両の条件を入力してください。',
      bigTruckCombination,
    ),
  big_volume_truck_threshold: yup
    .number()
    .integer('整数で入力してください')
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
    .test(
      'combination_validation',
      '詳細条件を設定した場合は、大型車両の条件を入力してください。',
      bigTruckCombination,
    ),
  big_weight_order_threshold: yup
    .number()
    .integer('整数で入力してください')
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  big_volume_order_threshold: yup
    .number()
    .integer('整数で入力してください')
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  small_weight_order_threshold: yup
    .number()
    .integer('整数で入力してください')
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  small_volume_order_threshold: yup
    .number()
    .integer('整数で入力してください')
    .positive('0以下は入力できません')
    .typeError('半角数で入力してください')
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
  company_trunk_transportation_setting_attributes: yup.object({
    short_rest_interval_hours: yup
      .number()
      .positive('0以下は入力できません')
      .typeError('半角数で入力してください')
      .nullable()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
      .test(
        'digit_point_validation',
        '小数点は2桁以内で入力してください。',
        digitPointValidator
      ),
    short_rest_duration_minutes: yup
      .number()
      .integer('整数で入力してください')
      .positive('0以下は入力できません')
      .typeError('半角数で入力してください')
      .nullable()
      .min(0, '0以上の値を設定してください。')
      .max(60, '60以下の値を設定してください。')
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value)),
    daily_available_working_duration_hours: yup
      .number()
      .positive('0以下は入力できません')
      .max(24, '24以下の値を設定してください。')
      .typeError('半角数で入力してください')
      .nullable()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
      .test(
        'digit_point_validation',
        '小数点は2桁以内で入力してください。',
        digitPointValidator
      ),
    daily_rest_duration_hours: yup
      .number()
      .positive('0以下は入力できません')
      .max(24, '24以下の値を設定してください。')
      .typeError('半角数で入力してください')
      .nullable()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
      .test(
        'digit_point_validation',
        '小数点は2桁以内で入力してください。',
        digitPointValidator
      )
      .test(
        'total_hours_validation',
        '労働時間と休憩時間の合計は24時間以内に設定してください。',
        function (value) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { daily_available_working_duration_hours } = this.parent;
          if (value && daily_available_working_duration_hours) {
            return (Number(value) + Number(daily_available_working_duration_hours)) <= 24;
          }
          return true;
        }
      ),
    }),
    company_order_time_ranges_attributes: yup.array().of(companyOrderTimeRange),
  });

const EngineConfigPresenter: FC = memo(() => {
  const licenseContext = useContext(LicenseContext);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const [companyId, setCompanyId] = useState(0);
  const { data: companyData } = useQueryCompany(companyId);

  const [orderTimeRangeAM, setOrderTimeRangeAM] = useState<CompanyOrderTimeRange>();
  const [orderTimeRangePM, setOrderTimeRangePM] = useState<CompanyOrderTimeRange>();
  const [orderTimeRangeALL, setOrderTimeRangeALL] = useState<CompanyOrderTimeRange>();

  const orderTimeRanges = [
    orderTimeRangeAM, orderTimeRangePM, orderTimeRangeALL
  ].filter((it) => it);

  useEffect(() => {
    if (!companyData) return;

    setOrderTimeRangeAM(
      companyData.company_order_time_ranges_attributes?.find((it) => it.kind === 'AM') || {
        id: null, kind: 'AM', start_at: null, end_at: null
      }
    );
    setOrderTimeRangePM(
      companyData.company_order_time_ranges_attributes?.find((it) => it.kind === 'PM') || {
        id: null, kind: 'PM', start_at: null, end_at: null
      }
    );
    setOrderTimeRangeALL(
      companyData.company_order_time_ranges_attributes?.find((it) => it.kind === 'ALL') || {
        id: null, kind: 'ALL', start_at: null, end_at: null
      }
    );
  }, [companyData]);

  const [customInputFieldOne, setCustomInputFieldOne] = useState<string>('');
  const updateCustomInputFieldOne = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (customInputFields.includes(event.target.value)) {
      enqueueSnackbar('既に存在する独自入力項目です', { variant: 'warning' });
      return;
    }

    setCustomInputFieldOne(
      event.target.value
    );
  };
  const [customInputFieldTwo, setCustomInputFieldTwo] = useState<string>('');
  const updateCustomInputFieldTwo = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (customInputFields.includes(event.target.value)) {
      enqueueSnackbar('既に存在する独自入力項目です', { variant: 'warning' });
      return;
    }

    setCustomInputFieldTwo(
      event.target.value
    );
  };
  const [customInputFieldThree, setCustomInputFieldTree] = useState<string>('');
  const updateCustomInputFieldThree = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (customInputFields.includes(event.target.value)) {
      enqueueSnackbar('既に存在する独自入力項目です', { variant: 'warning' });
      return;
    }

    setCustomInputFieldTree(
      event.target.value
    );
  };
  const [customInputFieldFour, setCustomInputFieldFour] = useState<string>('');
  const updateCustomInputFieldFour = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (customInputFields.includes(event.target.value)) {
      enqueueSnackbar('既に存在する独自入力項目です', { variant: 'warning' });
      return;
    }

    setCustomInputFieldFour(
      event.target.value
    );
  };
  const [customInputFieldFive, setCustomInputFieldFive] = useState<string>('');
  const updateCustomInputFieldFive = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (customInputFields.includes(event.target.value)) {
      enqueueSnackbar('既に存在する独自入力項目です', { variant: 'warning' });
      return;
    }

    setCustomInputFieldFive(
      event.target.value
    );
  };

  const customInputFields = [
    customInputFieldOne,
    customInputFieldTwo,
    customInputFieldThree,
    customInputFieldFour,
    customInputFieldFive,
  ].filter((maybe) => maybe);

  useEffect(() => {
    if (!companyData) return;

    setCustomInputFieldOne(
      companyData.custom_input_fields[0] || ''
    );
    setCustomInputFieldTwo(
      companyData.custom_input_fields[1] || ''
    );
    setCustomInputFieldTree(
      companyData.custom_input_fields[2] || ''
    );
    setCustomInputFieldFour(
      companyData.custom_input_fields[3] || ''
    );
    setCustomInputFieldFive(
      companyData.custom_input_fields[4] || ''
    );
  }, [companyData]);

  const {
    reset,
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors }
  } = useForm<LicenseEntity>({
    resolver: yupResolver(schema),
  });

  const { fields } = useFieldArray({
    control,
    name: 'packing_style_and_weight_volumes'
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const buttonOnClick: SubmitHandler<LicenseEntity> = async (data: LicenseEntity) => {
    const companyRequestPath = '/api/v2/company';

    data.delivery_list_grouping = deliverylListGrouping;
    data.custom_input_fields = customInputFields;
    data.travel_time_scale = ((travelTimeScale * -1) + 100.0) / 100;

    const { hoursToSeconds, minutesToSeconds } = datetimeUtil;
    const trunkTransportationSetting = data.company_trunk_transportation_setting_attributes;
    trunkTransportationSetting.short_rest_interval_seconds = hoursToSeconds(trunkTransportationSetting.short_rest_interval_hours);
    trunkTransportationSetting.short_rest_duration_seconds = minutesToSeconds(trunkTransportationSetting.short_rest_duration_minutes);
    trunkTransportationSetting.daily_available_working_duration_seconds = hoursToSeconds(trunkTransportationSetting.daily_available_working_duration_hours);
    trunkTransportationSetting.daily_rest_duration_seconds = hoursToSeconds(trunkTransportationSetting.daily_rest_duration_hours);

    setIsLoading(true);

    const loadingDefaultSeconds = loadingDefaultMinutes > 0 ? datetimeUtil.minutesToSeconds(loadingDefaultMinutes) : null;
    const unloadingDefaultSeconds = unloadingDefaultMinutes > 0 ? datetimeUtil.minutesToSeconds(unloadingDefaultMinutes) : null;

    data.company_order_time_ranges_attributes = data.company_order_time_ranges_attributes.map((it) => {
      if (!!it.start_at && !!it.end_at) return it;
      if (it.id) {
        return {
          ...it,
          _destroy: true,
        };
      }
      return null;
    }).filter((it) => it);
    const requestEntity = {
      ...data,
      loading_duration_seconds: loadingDefaultSeconds,
      unloading_duration_seconds: unloadingDefaultSeconds,
      loading_unloading_duration_table: loadingUnloadingDurations.map((it) => ({
        ...it, volumeM3: it.volumeM3 ? it.volumeM3 : null, loadingDurationSeconds: it.loadingDurationSeconds * 60, unloadingDurationSeconds: it.unloadingDurationSeconds * 60,
      }))
    };

    try {
      await axios.post(companyRequestPath, requestEntity);
      void queryClient.invalidateQueries({
        queryKey: ['company', { companyId }]
      });
      void queryClient.invalidateQueries({
        queryKey: ['useSessionRequest']
      });
      enqueueSnackbar('更新が完了しました');
    } catch (e) {
      enqueueSnackbar('エラーが発生しました');
      throw e;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!companyData) return;

    const { ...newCompanyData } = companyData;

    reset({
      ...newCompanyData,
      custom_input_fields: []
    });
  }, [companyData, reset]);

  useEffect(() => {
    if (!licenseContext.config) return;

    setCompanyId(licenseContext.config.selected_company_id);
  }, [licenseContext.config]);

  const [loadingDefaultMinutes, setLoadingDefaultMinutes] = useState<number>(0);
  const [unloadingDefaultMinutes, setUnloadingDefaultMinutes] = useState<number>(0);
  const [loadingUnloadingDurations, setLoadingUnloadingDurations] = useState<LoadingUnloadingDuration[]>([]);
  const [loadingUnloadingDurationErrors, setLoadingUnloadingDurationErrors] = useState<{ idx: number; key: string, message: string }[]>([]);

  const updateLoadingDefaultMinutes = (value: number) => {
    if (Number.isNaN(value)) return;
    if (value < 0) return;

    setLoadingDefaultMinutes(value);
  };

  const updateUnloadingDefaultMinutes = (value: number) => {
    if (Number.isNaN(value)) return;
    if (value < 0) return;

    setUnloadingDefaultMinutes(value);
  };

  const loadingUnloadingDurationDefault: LoadingUnloadingDuration = {
    weightKg: arrayUtil.max(loadingUnloadingDurations.map((it) => it.weightKg)) + 1,
    volumeM3: arrayUtil.max(loadingUnloadingDurations.map((it) => it.volumeM3)) + 1,
    loadingDurationSeconds: loadingDefaultMinutes || Math.ceil(arrayUtil.max(loadingUnloadingDurations.map((it) => it.loadingDurationSeconds))),
    unloadingDurationSeconds: unloadingDefaultMinutes || Math.ceil(arrayUtil.max(loadingUnloadingDurations.map((it) => it.unloadingDurationSeconds))),
  };

  const initializeLoadingUnloadingDuration = () => {
    setLoadingUnloadingDurations([loadingUnloadingDurationDefault]);
  };

  const addLoadingUnloadingDuration = (index: number) => {
    const newLoadingUnloadingDurations = loadingUnloadingDurations.map((loadingUnloadingDuration, i) => {
      if (i !== (index)) return loadingUnloadingDuration;
      return [
        loadingUnloadingDuration,
        loadingUnloadingDurationDefault
      ];
    });
    setLoadingUnloadingDurations(newLoadingUnloadingDurations.flat());
  };

  const removeLoadingUnloadingDuration = (index: number) => {
    const newLoadingUnloadingDurations = loadingUnloadingDurations.filter((_, i) => i !== index);
    setLoadingUnloadingDurations(newLoadingUnloadingDurations);
  };

  const updateLoadingUnloadingDuration = (index: number, key: keyof LoadingUnloadingDuration, value: string) => {
    const invalidValue = value !== '' && Number.isNaN(value);
    if (invalidValue) return;

    const newLoadingUnloadingDurations = loadingUnloadingDurations.map((loadingUnloadingDuration, i) => {
      if (i !== index) return loadingUnloadingDuration;
      return {
        ...loadingUnloadingDuration,
        [key]: value
      };
    });

    setLoadingUnloadingDurations(newLoadingUnloadingDurations);
  };

  useEffect(() => {
    if (!companyData) return;

    if (companyData.loading_duration_seconds) {
      setLoadingDefaultMinutes(companyData.loading_duration_seconds / 60);
    }

    if (companyData.unloading_duration_seconds) {
      setUnloadingDefaultMinutes(companyData.unloading_duration_seconds / 60);
    }

    if (companyData.loading_unloading_duration_table) {
      setLoadingUnloadingDurations(companyData.loading_unloading_duration_table.map((it) => ({
        ...it, loadingDurationSeconds: it.loadingDurationSeconds / 60, unloadingDurationSeconds: it.unloadingDurationSeconds / 60,
      })));
    } else {
      setLoadingUnloadingDurations([]);
    }

    if (companyData.delivery_list_grouping) {
      setDeliveryListGrouping(companyData.delivery_list_grouping);
    }
  }, [companyData]);

  useEffect(() => {
    const validateWeightKgPresent = (current: LoadingUnloadingDuration) => {
      if (!current.weightKg) {
        return '重量を入力してください';
      }
      return '';
    };

    const validateWeightKgGtPrev = (prev: LoadingUnloadingDuration | undefined, current: LoadingUnloadingDuration) => {
      if (prev?.weightKg && current.weightKg && Number(prev?.weightKg) > Number(current.weightKg)) {
        return '重量は小さい順に入力してください';
      }
      return '';
    };

    const validateVolumeM3GtPrev = (prev: LoadingUnloadingDuration | undefined, current: LoadingUnloadingDuration) => {
      if (!prev?.volumeM3) return '';

      if (Number(prev.volumeM3) > Number(current.volumeM3)) {
        return '立米は小さい順に入力してください';
      }
      return '';
    };

    const validateLoadingSecondsPresent = (current: LoadingUnloadingDuration) => {
      if (!current.loadingDurationSeconds) {
        return '積込時間を入力してください';
      }
      return '';
    };

    const validateUnloadingSecondsPresent = (current: LoadingUnloadingDuration) => {
      if (!current.unloadingDurationSeconds) {
        return '降地時間を入力してください';
      }
      return '';
    };

    const errs = loadingUnloadingDurations
      .flatMap((loadingUnloadingDuration, index) => {
        const prev = loadingUnloadingDurations[index - 1];

        const weightErrors = [
          validateWeightKgPresent(loadingUnloadingDuration),
          validateWeightKgGtPrev(prev, loadingUnloadingDuration)
        ].filter((maybe) => maybe);

        return [
          { idx: index, key: 'weightKg', message: weightErrors.join('、') },
          { idx: index, key: 'volumeM3', message: validateVolumeM3GtPrev(prev, loadingUnloadingDuration) },
          { idx: index, key: 'loadingDurationSeconds', message: validateLoadingSecondsPresent(loadingUnloadingDuration) },
          { idx: index, key: 'unloadingDurationSeconds', message: validateUnloadingSecondsPresent(loadingUnloadingDuration) }
        ].filter((err) => err.message !== '');
      });

    setLoadingUnloadingDurationErrors(errs);
  }, [loadingUnloadingDurations]);

  const [deliverylListGrouping, setDeliveryListGrouping] = useState<DeliveryListGroupingValues>('groupByAddress');
  const onChangeDeliveryListGrouping = (event: ChangeEvent<HTMLInputElement>) => {
    setDeliveryListGrouping(event.target.value as DeliveryListGroupingValues);
  };

  const [travelTimeScale, setTravelTimeScale] = useState<number>(0);
  useEffect(() => {
    if (!companyData) return;

    const val = companyData.travel_time_scale ?? 1;
    setTravelTimeScale((Math.round(val * 100.0) - 100) * -1);
  }, [companyData]);

  const tooltipProps = useMemo(() => (
    {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [20, -80]
          }
        }
      ]
    }
  ), []);

  const handleOrderTimeRangeChange = (kind: CompanyOrderTimeRangeKinds, key: string, value: string) => {
    if (kind === 'AM') {
      setOrderTimeRangeAM((prev) => ({
        ...prev,
        [key]: value,
      }));
    } else if (kind === 'PM') {
      setOrderTimeRangePM((prev) => ({
        ...prev,
        [key]: value,
      }));
    } else if (kind === 'ALL') {
      setOrderTimeRangeALL((prev) => ({
        ...prev,
        [key]: value,
      }));
    }
  };

  useEffect(() => {
    setValue('company_order_time_ranges_attributes', orderTimeRanges);
  }, [orderTimeRanges, setValue]);

  const contentHeight = useMemo(() => `calc(100vh - ${appBarHeight}px)`, []);

  return (
    <>
      <title>計算エンジン設定</title>
      <Box
        p={1}
        sx={{
          height: contentHeight,
          overflow: 'auto',
        }}
      >
        <Paper>
          <Stack
            component="form"
            noValidate
            spacing={3}
            divider={<Divider />}
            p={3}
            sx={{
              marginBottom: '80px',
              pb: 5,
            }}
          >
            <Stack>
              <Typography variant="h4" pb={2}>グローバル設定</Typography>
              <Stack direction="row" gap={2} justifyItems="center">
                <Stack direction="row" gap={2} justifyItems="center" sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    label="最大回転数"
                    id="shuttle_limit"
                    {...register('shuttle_limit')}
                    error={'shuttle_limit' in errors}
                    helperText={errors.shuttle_limit?.message}
                  />
                </Stack>
                <Stack>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    label="1回転あたりの最大納品先件数"
                    id="unloading_position_limit_by_a_shuttle"
                    {...register('unloading_position_limit_by_a_shuttle')}
                    error={'unloading_position_limit_by_a_shuttle' in errors}
                    helperText={errors.unloading_position_limit_by_a_shuttle?.message}
                  />
                </Stack>
              </Stack>
              <Stack direction="column">
                <Stack direction="row" gap={2} justifyItems="center" sx={{ mb: 2 }}>
                  <FormControl
                    variant="standard"
                  >
                    <Controller
                      control={control}
                      name="activate_constraint_load_after_not_cleared"
                      render={({ field }) => (
                        <FormControlLabel
                          {...field}
                          control={(
                            <Switch
                              checked={getValues('activate_constraint_load_after_not_cleared') || false}
                            />
                          )}
                          label="空荷時のみ積込を許可する"
                        />
                      )}
                    />
                  </FormControl>
                  <Stack direction="row" alignItems="center">
                    <FormControl
                      variant="standard"
                    >
                      <Controller
                        control={control}
                        name="load_after_initial_stock"
                        render={({ field }) => (
                          <FormControlLabel
                            {...field}
                            control={(
                              <Switch
                                checked={getValues('load_after_initial_stock') || false}
                              />
                            )}
                            label="宵積み時に納品前の積込を許可する"
                          />
                        )}
                      />
                    </FormControl>
                    <Tooltip
                      title="宵積み時、既存の荷物を降ろす前でもトラックに空きがあれば荷物を積み込むことができます。"
                    >
                      <HelpOutline />
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" alignItems="center">
                    <FormControl
                      variant="standard"
                    >
                      <Controller
                        control={control}
                        name="set_current_time_to_algorithm"
                        render={({ field }) => (
                          <FormControlLabel
                            {...field}
                            control={(
                              <Switch
                                checked={getValues('set_current_time_to_algorithm') || false}
                              />
                            )}
                            label="現在時刻より過去の配車計画は変更しない"
                          />
                        )}
                      />
                    </FormControl>
                    <Tooltip
                      title="OFFにすると、現在時刻よりも過去の計画も変更されます。"
                    >
                      <HelpOutline />
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" alignItems="center">
                    <FormControl
                      variant="standard"
                    >
                      <Controller
                        control={control}
                        name="load_start_at_main_warehouse"
                        render={({ field }) => (
                          <FormControlLabel
                            {...field}
                            control={(
                              <Switch
                                checked={getValues('load_start_at_main_warehouse') || false}
                              />
                            )}
                            label="主要倉庫から積み込みを開始する"
                          />
                        )}
                      />
                    </FormControl>
                  </Stack>
                </Stack>
              </Stack>
              <Stack
                sx={{
                  whiteSpace: 'nowrap',
                  mt: 1,
                }}
              >
                <Typography variant="h5">移動速度の調整</Typography>
              </Stack>
              <Stack direction="row" gap={2} justifyItems="center" sx={{ width: 400 }}>
                <Slider
                  step={1}
                  min={-50}
                  max={50}
                  valueLabelDisplay="auto"
                  // value={0}
                  value={travelTimeScale}
                  onChange={(_, value) => setTravelTimeScale(value)}
                />
                <Typography noWrap sx={{ width: 170 }}>
                  {`${Math.abs(travelTimeScale)}% ${travelTimeScale >= 0 ? '速くする' : '遅くする'}`}
                </Typography>
              </Stack>
              <Stack
                sx={{
                  whiteSpace: 'nowrap',
                  mt: 1,
                }}
              >
                <Typography variant="h5">時間帯の設定</Typography>
              </Stack>
              <Stack>
                <Table>
                  <TableBody>
                    {orderTimeRanges.sort((a, b) => (['AM', 'PM', 'ALL'].indexOf(a.kind) - ['AM', 'PM', 'ALL'].indexOf(b.kind))).map((it, index) => (
                      <TableRow key={`order-time-range-table-row-${it.kind}`}>
                        <TableCell width="50">
                          <Typography variant="h6">{it.kind}</Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="time"
                            variant="standard"
                            InputLabelProps={{ shrink: true }}
                            label=""
                            id={`order_time_range_start_at_${it.kind}`}
                            value={it.start_at || ''}
                            onChange={(event) => handleOrderTimeRangeChange(it.kind, 'start_at', event.target.value)}
                            error={errors.company_order_time_ranges_attributes?.[index]?.type === 'both-or-none' || !!errors.company_order_time_ranges_attributes?.[index]?.start_at}
                            helperText={errors.company_order_time_ranges_attributes?.[index]?.message || !!errors.company_order_time_ranges_attributes?.[index]?.start_at?.message}
                          />
                          〜
                          <TextField
                            size="small"
                            type="time"
                            variant="standard"
                            InputLabelProps={{ shrink: true }}
                            label=""
                            id={`order_time_range_end_at_${it.kind}`}
                            value={it.end_at || ''}
                            onChange={(event) => handleOrderTimeRangeChange(it.kind, 'end_at', event.target.value)}
                            error={!!errors.company_order_time_ranges_attributes?.[index]?.end_at}
                            helperText={errors.company_order_time_ranges_attributes?.[index]?.end_at?.message}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack>
                <Typography variant="h4" pb={2}>大型車両と荷物の割り付け条件設定</Typography>
                <Stack direction="row" sx={{ mb: 1 }}>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    label=""
                    id="big_weight_truck_threshold"
                    {...register('big_weight_truck_threshold')}
                    error={'big_weight_truck_threshold' in errors}
                    helperText={errors.big_weight_truck_threshold?.message}
                  />
                  <Typography variant="body1">kg 以上、または</Typography>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    label=""
                    id="big_volume_truck_threshold"
                    {...register('big_volume_truck_threshold')}
                    error={'big_volume_truck_threshold' in errors}
                    helperText={errors.big_volume_truck_threshold?.message}
                  />
                  <Typography variant="body1">m3以上の車両に対して</Typography>
                </Stack>
                <Stack direction="row" sx={{ ml: 2, mb: 1 }}>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    label=""
                    id="big_weight_order_threshold"
                    {...register('big_weight_order_threshold')}
                    error={'big_weight_order_threshold' in errors}
                    helperText={errors.big_weight_order_threshold?.message}
                  />
                  <Typography variant="body1">kg・</Typography>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    label=""
                    id="big_volume_order_threshold"
                    {...register('big_volume_order_threshold')}
                    error={'big_volume_order_threshold' in errors}
                    helperText={errors.big_volume_order_threshold?.message}
                  />
                  <Typography variant="body1">m3以上の荷物を優先的に割り付ける</Typography>
                  <Tooltip
                    title="納品地点ごとに合算した荷物量の数字"
                    PopperProps={tooltipProps}
                  >
                    <HelpOutline sx={{ ml: 1 }} />
                  </Tooltip>
                </Stack>
                <Stack direction="row" sx={{ ml: 2 }}>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    label=""
                    id="small_weight_order_threshold"
                    {...register('small_weight_order_threshold')}
                    error={'small_weight_order_threshold' in errors}
                    helperText={errors.small_weight_order_threshold?.message}
                  />
                  <Typography variant="body1">kg・</Typography>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    label=""
                    id="small_volume_order_threshold"
                    {...register('small_volume_order_threshold')}
                    error={'small_volume_order_threshold' in errors}
                    helperText={errors.small_volume_order_threshold?.message}
                  />
                  <Typography variant="body1">m3以下の荷物は割り付けない</Typography>
                  <Tooltip
                    title="納品地点ごとに合算した荷物量の数字"
                    PopperProps={tooltipProps}
                  >
                    <HelpOutline sx={{ ml: 1 }} />
                  </Tooltip>
                </Stack>
              </Stack>
            </Stack>
            <Stack>
              <Typography variant="h4" pb={2}>休憩時間設定 (幹線輸送)</Typography>
              <Stack>
                <Stack sx={{ mb: 1 }}>
                  <Typography variant="h6">連続運転時間設定</Typography>
                  <Stack direction="row" sx={{ mb: 1, gap: 1 }} alignItems="baseline">
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      InputLabelProps={{ shrink: true, }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">時間</InputAdornment>,
                        inputProps: {
                          min: 0,
                          max: 24,
                        }
                      }}
                      sx={{ minWidth: 120 }}
                      label="連続運転時間"
                      id="company_trunk_transportation_setting_attributes.short_rest_interval_hours"
                      {...register('company_trunk_transportation_setting_attributes.short_rest_interval_hours')}
                      error={!!errors.company_trunk_transportation_setting_attributes?.short_rest_interval_hours}
                      helperText={errors.company_trunk_transportation_setting_attributes?.short_rest_interval_hours?.message}
                    />
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">分</InputAdornment>
                      }}
                      label="休憩時間"
                      id="company_trunk_transportation_setting_attributes.short_rest_duration_minutes"
                      {...register('company_trunk_transportation_setting_attributes.short_rest_duration_minutes')}
                      error={!!errors.company_trunk_transportation_setting_attributes?.short_rest_duration_minutes}
                      helperText={errors.company_trunk_transportation_setting_attributes?.short_rest_duration_minutes?.message}
                    />
                    <Typography>例) 連続運転時間「4時間」で、「30分」の休憩を取得する</Typography>
                  </Stack>
                </Stack>
                <Stack sx={{ mb: 1 }}>
                  <Typography variant="h6">1日の労働時間後の休憩時間</Typography>
                  <Stack direction="row" sx={{ mb: 1, gap: 1 }} alignItems="baseline">
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">時間</InputAdornment>,
                        inputProps: {
                          min: 0,
                          max: 24,
                        }
                      }}
                      sx={{ minWidth: 120 }}
                      label="1日の労働時間"
                      id="company_trunk_transportation_setting_attributes.daily_available_working_duration_hours"
                      {...register('company_trunk_transportation_setting_attributes.daily_available_working_duration_hours')}
                      error={!!errors.company_trunk_transportation_setting_attributes?.daily_available_working_duration_hours}
                      helperText={errors.company_trunk_transportation_setting_attributes?.daily_available_working_duration_hours?.message}
                    />
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">時間</InputAdornment>,
                        inputProps: {
                          min: 0,
                          max: 24,
                        }
                      }}
                      sx={{ minWidth: 120 }}
                      label="休息時間"
                      id="company_trunk_transportation_setting_attributes.daily_rest_duration_hours"
                      {...register('company_trunk_transportation_setting_attributes.daily_rest_duration_hours')}
                      error={!!errors.company_trunk_transportation_setting_attributes?.daily_rest_duration_hours}
                      helperText={errors.company_trunk_transportation_setting_attributes?.daily_rest_duration_hours?.message}
                    />
                    <Typography>例) 労働時間「10時間」で、「14時間」の休息時間を取得する</Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
            <Stack>
              <Typography variant="h4" pb={2}>コスト設定</Typography>
              <Stack direction="row" gap={2} justifyItems="center">
                <Stack>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    label="時給"
                    id="driver_cost_yen_per_hours"
                    {...register('driver_cost_yen_per_hours')}
                    error={'driver_cost_yen_per_hours' in errors}
                    helperText={errors.driver_cost_yen_per_hours?.message}
                  />
                </Stack>
                <Stack>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    label="1kmあたりの燃料費"
                    id="truck_fuel_cost_yen_per_km"
                    {...register('truck_fuel_cost_yen_per_km')}
                    error={'truck_fuel_cost_yen_per_km' in errors}
                    helperText={errors.truck_fuel_cost_yen_per_km?.message}
                  />
                </Stack>
                <Stack>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    label="1日あたりの保険料"
                    id="truck_insurance_fee_yen_per_day"
                    {...register('truck_insurance_fee_yen_per_day')}
                    error={'truck_insurance_fee_yen_per_day' in errors}
                    helperText={errors.truck_insurance_fee_yen_per_day?.message}
                  />
                </Stack>
                <Stack>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    label="1日あたりの車両修繕費"
                    id="truck_repair_cost_yen_per_day"
                    {...register('truck_repair_cost_yen_per_day')}
                    error={'truck_repair_cost_yen_per_day' in errors}
                    helperText={errors.truck_repair_cost_yen_per_day?.message}
                  />
                </Stack>
                <Stack>
                  <TextField
                    size="small"
                    type="number"
                    variant="standard"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    label="配送計画あたりの高速代"
                    id="truck_expressway_fee_yen_per_shift"
                    {...register('truck_expressway_fee_yen_per_shift')}
                    error={'truck_expressway_fee_yen_per_shift' in errors}
                    helperText={errors.truck_expressway_fee_yen_per_shift?.message}
                  />
                </Stack>
              </Stack>
            </Stack>
            <Stack
              spacing={2}
            >
              <Typography variant="h4">表示設定</Typography>
              <Stack>
                <Typography variant="h6">
                  配送リストの地点表示
                </Typography>
                <FormControl
                  fullWidth
                >
                  <RadioGroup
                    row
                    value={deliverylListGrouping ?? 'groupByAddress'}
                    onChange={onChangeDeliveryListGrouping}
                  >
                    <FormControlLabel value="groupByAddress" control={<Radio />} label="住所でまとめる" />
                    <FormControlLabel value="groupByName" control={<Radio />} label="名称でまとめる" />
                  </RadioGroup>
                </FormControl>
              </Stack>
              <Typography variant="h6">
                配送指示書に定型文を表示
              </Typography>
              <TextareaAutosize
                style={{ width: '80%' }}
                id="operation_direction_memo"
                {...register('operation_direction_memo')}
                minRows={3}
              />
            </Stack>
            <Stack
              spacing={2}
            >
              <Typography variant="h4">荷捌き時間設定</Typography>
              <Stack>
                <Typography variant="h6">
                  デフォルト値設定
                </Typography>
                <Stack
                  direction="row"
                  gap={2}
                  justifyItems="center"
                >
                  <Stack>
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      label="積込時間(分)"
                      value={loadingDefaultMinutes || ''}
                      onChange={(e) => updateLoadingDefaultMinutes(Number(e.target.value))}
                    />
                  </Stack>
                  <Stack>
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      label="降地時間(分)"
                      value={unloadingDefaultMinutes || ''}
                      onChange={(e) => updateUnloadingDefaultMinutes(Number(e.target.value))}
                    />
                  </Stack>
                </Stack>
              </Stack>

              <Stack>
                {!loadingUnloadingDurations.length ? (
                  <Stack
                    direction="row"
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        initializeLoadingUnloadingDuration();
                      }}
                    >
                      重量・体積ごとの個別荷捌き時間を設定する
                    </Button>
                  </Stack>
                ) : (
                  <Typography variant="h6">
                    個別設定
                  </Typography>
                )}
                {!!loadingUnloadingDurations.length && loadingUnloadingDurations.map((loadingUnloadingDuration, index) => (
                  <Stack
                    key={[
                      'loadingUnloadingDuration',
                      index
                    ].join('-')} direction="row"
                    gap={2}
                    pb={2}
                    alignItems="center"
                  >
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      label="～重量(Kg)"
                      value={loadingUnloadingDuration.weightKg ?? ''}
                      onChange={(e) => updateLoadingUnloadingDuration(index, 'weightKg', e.target.value)}
                      error={loadingUnloadingDurationErrors.some((err) => err.idx === index && err.key === 'weightKg')}
                      helperText={
                        loadingUnloadingDurationErrors.find((err) => err.idx === index && err.key === 'weightKg')?.message
                      }
                    />
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      label="～体積(M3)"
                      value={loadingUnloadingDuration.volumeM3 ?? ''}
                      onChange={(e) => updateLoadingUnloadingDuration(index, 'volumeM3', e.target.value)}
                      error={loadingUnloadingDurationErrors.some((err) => err.idx === index && err.key === 'volumeM3')}
                      helperText={
                        loadingUnloadingDurationErrors.find((err) => err.idx === index && err.key === 'volumeM3')?.message
                      }
                    />
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      label="積込時間(分)"
                      value={loadingUnloadingDuration.loadingDurationSeconds}
                      onChange={(e) => updateLoadingUnloadingDuration(index, 'loadingDurationSeconds', e.target.value)}
                      error={loadingUnloadingDurationErrors.some((err) => err.idx === index && err.key === 'loadingDurationSeconds')}
                      helperText={
                        loadingUnloadingDurationErrors.find((err) => err.idx === index && err.key === 'loadingDurationSeconds')?.message
                      }
                    />
                    <TextField
                      size="small"
                      type="number"
                      variant="standard"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      label="降地時間(分)"
                      value={loadingUnloadingDuration.unloadingDurationSeconds}
                      onChange={(e) => updateLoadingUnloadingDuration(index, 'unloadingDurationSeconds', e.target.value)}
                      error={loadingUnloadingDurationErrors.some((err) => err.idx === index && err.key === 'unloadingDurationSeconds')}
                      helperText={
                        loadingUnloadingDurationErrors.find((err) => err.idx === index && err.key === 'unloadingDurationSeconds')?.message
                      }
                    />
                    <IconButton
                      onClick={() => addLoadingUnloadingDuration(index)}
                      disabled={loadingUnloadingDurationErrors.length > 0}
                    >
                      <AddCircleOutlineOutlined />
                    </IconButton>
                    <IconButton
                      onClick={() => removeLoadingUnloadingDuration(index)}
                    >
                      <RemoveCircleOutlineOutlined />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
            </Stack>
            <Stack>
              <Typography variant="h4" gutterBottom>
                荷姿ごとの変換ルール
              </Typography>
              <Typography pb={2} variant="caption">
                {`案件登録時に${companyData?.unit_used_for_calculation}が未指定の場合、指定された変換ルールが適応され${companyData?.unit_used_for_calculation}が補完されます。`}
              </Typography>
              <TableContainer
                component={Paper}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        項目
                      </TableCell>
                      <TableCell>
                        {`1個あたりの${companyData?.unit}`}
                      </TableCell>
                      {companyData?.unit_used_for_calculation === '重量と体積' && (
                        <TableCell>
                          1個あたりの体積(m3)
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {fields.map((_field, idx) => (
                      <TableRow key={getValues(`packing_style_and_weight_volumes.${idx}.name`)}>
                        <TableCell>
                          {getValues(`packing_style_and_weight_volumes.${idx}.name`)}
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="standard"
                            fullWidth
                            type="number"
                            required
                            {...register(`packing_style_and_weight_volumes.${idx}.weight`)}
                            error={errors?.packing_style_and_weight_volumes && !!errors?.packing_style_and_weight_volumes[idx]?.weight}
                            helperText={errors?.packing_style_and_weight_volumes && errors?.packing_style_and_weight_volumes[idx]?.weight?.message}
                          />
                        </TableCell>
                        {companyData?.unit_used_for_calculation === '重量と体積' && (
                          <TableCell>
                            <TextField
                              size="small"
                              variant="standard"
                              type="number"
                              fullWidth
                              {...register(`packing_style_and_weight_volumes.${idx}.volume`)}
                              error={errors?.packing_style_and_weight_volumes && !!errors?.packing_style_and_weight_volumes[idx]?.volume}
                              helperText={errors?.packing_style_and_weight_volumes && errors?.packing_style_and_weight_volumes[idx]?.volume?.message}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
            <Stack
              sx={{ mb: 40 }}
            >
              <Typography variant="h4" gutterBottom>
                項目設定
              </Typography>
              <Typography pb={2} variant="caption">
                案件情報に任意の入力項目を最大5つ設定することができます。
              </Typography>
              <Stack
                direction={{ sm: 'column', md: 'row' }}
                gap={1}
                spacing={1}
              >
                <TextField
                  size="small"
                  fullWidth
                  variant="standard"
                  value={customInputFieldOne}
                  onChange={updateCustomInputFieldOne}
                />
                <TextField
                  size="small"
                  fullWidth
                  variant="standard"
                  value={customInputFieldTwo}
                  onChange={updateCustomInputFieldTwo}
                />
                <TextField
                  size="small"
                  fullWidth
                  variant="standard"
                  value={customInputFieldThree}
                  onChange={updateCustomInputFieldThree}
                />
                <TextField
                  size="small"
                  fullWidth
                  variant="standard"
                  value={customInputFieldFour}
                  onChange={updateCustomInputFieldFour}
                />
                <TextField
                  size="small"
                  fullWidth
                  variant="standard"
                  value={customInputFieldFive}
                  onChange={updateCustomInputFieldFive}
                />
              </Stack>
            </Stack>
          </Stack>
          <Stack
            alignItems="center"
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100%',
              padding: 2,
              bgcolor: 'background.paper',
              boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.15)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            {
              !!loadingUnloadingDurationErrors.length && (
                <Typography color="error" variant="caption">
                  荷捌き時間設定にエラーがあります。修正してください。
                </Typography>
              )
            }
            {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
            <LoadingButton fullWidth disabled={loadingUnloadingDurationErrors.length > 0} loading={isLoading} variant="contained" size="large" onClick={handleSubmit(buttonOnClick)}>
              保存する
            </LoadingButton>
          </Stack>
        </Paper>
      </Box>
    </>
  );
});

export default EngineConfigPresenter;
